import React, { useMemo, useState, useEffect, useRef } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useGetTagValues } from '@src/services/query/applang/queryHooks';
import { AutoComplete as AutoCompleteStyled } from './styled';
import { useCriticalEventInterval } from '@src/features/CriticalEventFormContainer';

interface TagValueAutoCompleteProps {
  rawEventName?: string;
  rule: any;
  onChange?: (option: any) => void;
  placeholder?: string;
  filterOption?: boolean;
  status?: 'error' | 'success';
  disabled?: boolean;
  [key: string]: any;
}

const TagValueAutoComplete: React.FC<TagValueAutoCompleteProps> = ({
  rawEventName,
  rule,
  onChange,
  ...rest
}) => {
  const value = rule.tag_value.value[0];
  const label = rule.tag_value.label?.[0];
  const interval = useCriticalEventInterval();

  // State for the current input value (allows manual typing)
  const [inputValue, setInputValue] = useState<string>('');
  // State for search input (immediate update for UI)
  const [searchInput, setSearchInput] = useState<string>('');
  // Debounced search key for API calls
  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track dropdown visibility to control tooltip
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Sync inputValue with rule value when it changes externally
  useEffect(() => {
    if (value !== undefined) {
      // If we have a label, prefer showing label, otherwise show value
      const displayText = label || value;
      setInputValue(displayText);
    } else {
      setInputValue('');
    }
  }, [value, label]);

  // Debounce search input
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchKey(searchInput);
    }, 300); // 300ms debounce

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Get tag key from rule
  const tagKey = rule.tag_key.value[0];

  // Fetch tag values from API with debounced search key
  const { data: tagValues, isFetching } = useGetTagValues(
    {
      event_name: rawEventName || undefined,
      time_range: interval,
      tag_key: tagKey,
      key: debouncedSearchKey, // Use debounced search key for filtering
    },
    { enabled: !!isDropdownOpen },
  );

  // Transform tag values to options format
  const tagValueOptions = useMemo(() => {
    if (!tagValues || tagValues.length === 0) return [];
    return tagValues.map((val) => ({
      label: val.label,
      value: val.val,
      title: null,
    }));
  }, [tagValues]);

  const Suffix = useMemo(() => {
    if (isFetching) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return undefined;
  }, [isFetching]);

  return (
    <Tooltip
      title={inputValue || ''}
      getPopupContainer={() => document.body}
      mouseEnterDelay={0.5}
      open={!isDropdownOpen && inputValue ? undefined : false}
    >
      <AutoCompleteStyled
        labelInValue={true}
        optionRender={(option) => (
          <Tooltip title={option.label}>
            <span style={{ width: '100%', display: 'inline-block' }}>{option.label}</span>
          </Tooltip>
        )}
        value={inputValue}
        options={tagValueOptions}
        suffixIcon={Suffix}
        style={{ width: '100%' }}
        onOpenChange={setIsDropdownOpen}
        onSearch={(searchValue) => {
          // Update input value immediately for typing
          setInputValue(searchValue || '');
          // Update search input (will be debounced for API calls)
          setSearchInput(searchValue || '');
        }}
        {...rest}
        onChange={(selectedValue, option) => {
          const selectedObj = selectedValue as
            | { label?: string; value?: string }
            | string
            | undefined;
          const newValue = typeof selectedObj === 'string' ? selectedObj : selectedObj?.value;
          // Use label for display, fallback to value if label not available
          const displayText =
            typeof selectedObj === 'string'
              ? selectedObj
              : selectedObj?.label || selectedObj?.value;
          setInputValue(displayText || '');

          // When an option is selected from dropdown, use the option object
          if (option && typeof option === 'object' && 'value' in option) {
            onChange?.(option);
          } else if (newValue) {
            // User typed a custom value - create option with value and label the same
            onChange?.({
              value: newValue,
              label: newValue,
            });
          } else {
            // Clear case
            onChange?.(undefined);
          }
        }}
        onBlur={() => {
          // When user finishes typing, save the current input value
          if (inputValue && inputValue !== value) {
            // Check if it matches an existing option
            const matchedOption = tagValueOptions.find(
              (opt) => opt.value === inputValue || opt.label === inputValue,
            );
            if (matchedOption) {
              onChange?.(matchedOption);
            } else {
              // Custom typed value
              onChange?.({
                value: inputValue,
                label: inputValue,
              });
            }
          }
        }}
        filterOption={false} // Disable client-side filtering, use server-side
        allowClear={true}
        popupMatchSelectWidth={false}
        styles={{
          popup: {
            root: {
              maxWidth: 'min(700px, 45vw)',
            },
          },
        }}
      />
    </Tooltip>
  );
};

export default TagValueAutoComplete;
